 package com.karawek.core.auth.presenter

/**
 * Progressive wait schedule (in seconds) applied after each successful resend.
 * Index 0 → after 1st resend: 60 s
 * Index 1 → after 2nd resend: 120 s
 * Index 2 → after 3rd resend: 300 s
 * Attempting a 4th resend triggers a session-wide lockout.
 */
internal val RESEND_WAIT_SCHEDULE = listOf(60, 120, 300)

/**
 * Pure Kotlin class that enforces OTP-resend rate limiting.
 *
 * The presenter owns an instance via `remember { OtpResendController() }` and calls
 * [tick] once per second from a `LaunchedEffect`. The presenter then mirrors
 * [countdownSeconds], [isLockedOut], and [attempts] into Compose `mutableStateOf`
 * vars so the UI reacts to changes.
 */
class OtpResendController {

    /** Number of successful resend attempts recorded this session. */
    var attempts: Int = 0
        private set

    /** Remaining seconds the user must wait before the next resend is permitted. */
    var countdownSeconds: Int = 0
        private set

    /** True once the user has exhausted all permitted resend attempts. */
    var isLockedOut: Boolean = false
        private set

    /** True when the user is allowed to resend right now. */
    val canResend: Boolean
        get() = !isLockedOut && countdownSeconds == 0

    /**
     * Records a resend attempt and starts the appropriate countdown.
     *
     * @return `true` if the attempt is permitted and the OTP API should be called;
     *         `false` if the attempt is blocked (countdown still running, or locked out).
     *         When `false` is returned due to lockout, [isLockedOut] will be `true`.
     */
    fun recordAttempt(): Boolean {
        if (isLockedOut || !canResend) return false

        if (attempts >= RESEND_WAIT_SCHEDULE.size) {
            isLockedOut = true
            return false
        }

        countdownSeconds = RESEND_WAIT_SCHEDULE[attempts]
        attempts++
        return true
    }

    /**
     * Advances the countdown by one second.
     * Should be called once per second from a coroutine in the presenter.
     */
    fun tick() {
        if (countdownSeconds > 0) {
            countdownSeconds--
        }
    }
}
